import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaEye } from 'react-icons/fa';
import axiosPost from '../../../helpers/axios-helper';
import axios from 'axios';
import config from '../../../constants/config';
import { compressImage } from '../../../helpers/imageCompressor';
import { LAW_MARKERS } from '../../../components/LawAtAGlance';

// Mock data for countries
const mockCountries = [
  { 
    id: 1, 
    name: 'Jamaica', 
    info: 'Known for its beautiful beaches, reggae music, and vibrant culture.',
    active: 1
  },
  { 
    id: 2, 
    name: 'Barbados', 
    info: 'Famous for pristine beaches, rum, and being the birthplace of Rihanna.',
    active: 1
  },
  { 
    id: 3, 
    name: 'Trinidad and Tobago', 
    info: 'Known for its carnival celebrations, calypso music, and diverse culture.',
    active: 0
  },
  { 
    id: 4, 
    name: 'Bahamas', 
    info: 'An archipelago known for its clear waters, coral reefs, and luxury resorts.',
    active: 1
  },
  { 
    id: 5, 
    name: 'Cuba', 
    info: 'Famous for its vintage cars, cigars, rum, and vibrant music scene.',
    active: 0
  },
  { 
    id: 6, 
    name: 'Curaçao', 
    info: 'Known for its colorful architecture, coral reefs, and diverse heritage.',
    active: 1
  }
];

export default function Countries() {
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    continent: 'Caribbean',
    summary: '',
    info: '',
    image: '',
    active: 0,
    law_relations: '',
    law_marriage: '',
    law_protection: '',
    law_gender_id: '',
    law_checked: '',
  });
  const [uploading, setUploading] = useState(false);

  const loadCountries = () => {
    const controller = new AbortController();
    setIsLoading(true);
    axiosPost(
      'dashboard/countries/list',
      {},
      {},
      (res) => {
        const arr = Array.isArray(res) ? res : (res?.data || []);
        const mapped = arr.map((c, i) => ({
          id: c.id ?? i + 1,
          name: c.name || c.title || '-',
          continent: c.continent || 'Caribbean',
          summary: c.summary || '',
          info: c.info || c.description || '',
          image: c.image || '',
          active: Number(c.active ?? 1),
          law_relations: c.law_relations || '',
          law_marriage: c.law_marriage || '',
          law_protection: c.law_protection || '',
          law_gender_id: c.law_gender_id || '',
          law_checked: c.law_checked || ''
        }));
        setCountries(mapped);
        setIsLoading(false);
      },
      controller
    );
    return () => controller.abort();
  };

  useEffect(() => {
    const cancel = loadCountries();
    return cancel;
  }, []);

  const handleEdit = (country) => {
    setSelectedCountry(country);
    setFormData(country);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      setCountries(countries.filter(country => country.id !== id));
    }
  };

  const handleAddNew = () => {
    setSelectedCountry(null);
    setFormData({
      name: '',
      continent: 'Caribbean',
      summary: '',
      info: '',
      image: '',
      active: 0,
      law_relations: '',
      law_marriage: '',
      law_protection: '',
      law_gender_id: '',
      law_checked: '',
    });
    setIsEditing(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const endpoint = selectedCountry ? 'countries/update' : 'countries/create';
    const payload = {
      ...(selectedCountry ? { id: selectedCountry.id } : {}),
      name: formData.name,
      continent: formData.continent,
      summary: formData.summary,
      info: formData.info,
      image: formData.image,
      active: Number(formData.active),
      law_relations: formData.law_relations || '',
      law_marriage: formData.law_marriage || '',
      law_protection: formData.law_protection || '',
      law_gender_id: formData.law_gender_id || '',
      law_checked: formData.law_checked || ''
    };
    axiosPost(endpoint, {}, payload, (res) => {
      // After success, reload list and close form
      loadCountries();
      setIsEditing(false);
      setSelectedCountry(null);
    });
  };

  const toAbsoluteUrl = (url) => {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return `${config.api.url}${url}`;
  };

  const handleImageFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      // Compress image before uploading
      const compressedFile = await compressImage(file, { maxWidth: 1920, maxHeight: 1080, quality: 0.8 });
      const form = new FormData();
      form.append('image', compressedFile);
      const { data } = await axios.post(
        `${config.api.url}countries/upload-image`,
        form,
        {
          headers: {
            'api': config.api.key
          }
        }
      );
      if (data && data.success && data.data && data.data.url) {
        const abs = toAbsoluteUrl(data.data.url);
        setFormData((prev) => ({ ...prev, image: abs }));
      } else {
        console.error('Upload failed', data);
      }
    } catch (err) {
      console.error('Upload error', err);
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsViewing(false);
  };

  const handleView = (country) => {
    setSelectedCountry(country);
    setIsViewing(true);
  };

  return (
    <div>
      <div className="DashboardPanel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className="DashboardTitle">Countries Management</h1>
          <button className="DashboardButton" onClick={handleAddNew}>
            <FaPlus style={{ marginRight: '5px' }} /> Add Country
          </button>
        </div>
        
        {isLoading ? (
          <div>Loading...</div>
        ) : isViewing ? (
          <div className="DashboardDetailView">
            <h2>{selectedCountry.name}</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
                <h3 style={{ marginBottom: '15px' }}>Country Information</h3>
                
                <div style={{ marginBottom: '10px' }}>
                  <strong>Name:</strong> {selectedCountry.name}
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <strong>Summary:</strong> {selectedCountry.summary || '-'}
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <strong>Info:</strong> {selectedCountry.info}
                </div>
                {selectedCountry.image && (
                  <div style={{ marginBottom: '10px' }}>
                    <strong>Image:</strong><br />
                    <img src={selectedCountry.image} alt={selectedCountry.name} style={{ maxWidth: '240px', borderRadius: 6 }} />
                  </div>
                )}
                
                <div style={{ marginBottom: '10px' }}>
                  <strong>Active:</strong> {selectedCountry.active ? 'Yes' : 'No'}
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="DashboardButtonSecondary" onClick={handleCancel}>
                Back to List
              </button>
              <button className="DashboardButton" onClick={() => handleEdit(selectedCountry)}>
                Edit Country
              </button>
            </div>
          </div>
        ) : isEditing ? (
          <form className="DashboardForm" onSubmit={handleSubmit}>
            <h2 className="DashboardSubtitle">
              {selectedCountry ? 'Edit Country' : 'Add New Country'}
            </h2>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="DashboardFormInput"
                required
              />
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Continent</label>
              <select
                name="continent"
                value={formData.continent}
                onChange={handleChange}
                className="DashboardFormInput"
                required
              >
                <option value="Caribbean">Caribbean</option>
                <option value="Central America">Central America</option>
                <option value="South America">South America</option>
                <option value="North America">North America</option>
              </select>
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Summary</label>
              <textarea
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                className="DashboardFormInput"
                rows="3"
                placeholder="Short summary displayed on the country page (max 5 lines)"
              />
            </div>

            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Full Info / Travel Guide</label>
              <textarea
                name="info"
                value={formData.info}
                onChange={handleChange}
                className="DashboardFormInput"
                rows="10"
                required
              />
            </div>

            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Image URL</label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleChange}
                className="DashboardFormInput"
                placeholder="https://..."
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                <input type="file" accept="image/*" onChange={handleImageFile} />
                {uploading && <span>Uploading...</span>}
              </div>
              {formData.image && (
                <div style={{ marginTop: 10 }}>
                  <img src={formData.image} alt="Preview" style={{ maxWidth: 240, borderRadius: 6 }} />
                </div>
              )}
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Active</label>
              <select
                name="active"
                value={formData.active}
                onChange={handleChange}
                className="DashboardFormInput"
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </div>
            
            <h3 style={{ marginTop: 30 }}>The law at a glance</h3>
            <p style={{ color: '#666', marginTop: 0 }}>
              Shown as a bar above the LGBTQ+ Organisations on the country page. Leave a marker on
              "Not set" until it has been checked; it is then hidden. With nothing set, the bar is hidden.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
              {[
                ['law_relations', 'relations'],
                ['law_marriage', 'marriage'],
                ['law_protection', 'protection'],
                ['law_gender_id', 'genderId'],
              ].map(([field, key]) => {
                const marker = LAW_MARKERS.find((m) => m.key === key);
                return (
                  <div className="DashboardFormGroup" key={field}>
                    <label className="DashboardFormLabel">{marker.label}</label>
                    <select name={field} value={formData[field] || ''} onChange={handleChange} className="DashboardFormInput">
                      <option value="">Not set</option>
                      {Object.entries(marker.values).map(([value, v]) => (
                        <option key={value} value={value}>
                          {(v.dot === 'full' ? '● ' : v.dot === 'half' ? '◐ ' : '○ ') + v.text}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Last checked</label>
                <input type="month" name="law_checked" value={formData.law_checked || ''} onChange={handleChange} className="DashboardFormInput" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="DashboardButtonSecondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="DashboardButton">
                {selectedCountry ? 'Update Country' : 'Add Country'}
              </button>
            </div>
          </form>
        ) : (
          <table className="DashboardTable">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Info</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {countries.map(country => (
                <tr key={country.id}>
                  <td>{country.image ? (<img src={country.image} alt={country.name} style={{ width: 56, height: 40, objectFit: 'cover', borderRadius: 4 }} />) : null}</td>
                  <td>{country.name}</td>
                  <td>{country.info.length > 50 ? country.info.substring(0, 50) + '...' : country.info}</td>
                  <td>{country.active ? 'Yes' : 'No'}</td>
                  <td>
                    <button 
                      onClick={() => handleView(country)} 
                      style={{ background: 'none', cursor: 'pointer', marginRight: '10px' }}
                      title="View Details"
                    >
                      <FaEye color="#4caf50" />
                    </button>
                    <button 
                      onClick={() => handleEdit(country)} 
                      style={{ background: 'none', cursor: 'pointer', marginRight: '10px' }}
                      title="Edit Country"
                    >
                      <FaEdit color="#1976d2" />
                    </button>
                    <button 
                      onClick={() => handleDelete(country.id)} 
                      style={{ background: 'none', cursor: 'pointer' }}
                      title="Delete Country"
                    >
                      <FaTrash color="#e65100" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
